/**
 * Household data (docs/04-api.md "Households"). `/me` only carries `householdId`s (docs/03 §3.2
 * keeps auth and households as separate modules with no back-reference), so the switcher and
 * onboarding enrich with names client-side, one `GET /households/:hid` per membership — fine at
 * household-per-user counts in the single digits.
 */
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import type { BucketDef } from '@finbob/shared';
import { api } from '@/lib/api-client';
import { meQueryKey } from '@/features/auth/api';

export interface Person {
  email?: string;
  avatarUrl?: string;
  id: string;
  name: string;
  userId?: string;
  role: 'adult' | 'child';
  colorIndex?: number;
}

export interface Household {
  id: string;
  name: string;
  currency: string;
  locale: string;
  timezone: string;
  buckets: BucketDef[];
  settings: { monthOpenDay: number; settleByDay: number; safeAccessAccountId?: string };
  people: Person[];
  ownerUserId: string;
  createdAt: string;
  updatedAt: string;
}

export function useHousehold(householdId: string | undefined) {
  return useQuery({
    queryKey: ['households', householdId] as const,
    staleTime: 0,
    refetchOnWindowFocus: 'always',
    queryFn: () => api.get<Household>(`/households/${householdId}`),
    enabled: !!householdId,
  });
}

/** For the switcher: resolves a name for each of the caller's household ids. */
export function useHouseholdNames(householdIds: string[]) {
  return useQueries({
    queries: householdIds.map((id) => ({
      queryKey: ['households', id] as const,
      queryFn: () => api.get<Household>(`/households/${id}`),
      staleTime: 5 * 60_000,
    })),
  });
}

export interface CreateHouseholdInput {
  name: string;
  currency?: string;
  timezone?: string;
}

export function useCreateHousehold() {
  const queryClient = useQueryClient();
  return useMutation({
    meta: { successMessage: 'Household added' },
    mutationFn: (input: CreateHouseholdInput) => api.post<Household>('/households', input),
    onSuccess: () => {
      // memberships changed — /me needs refetching before routing off the result.
      void queryClient.invalidateQueries({ queryKey: meQueryKey });
    },
  });
}

export interface PutBucketsInput {
  buckets: BucketDef[];
  reassignments?: Record<string, string>;
}

/**
 * Also invalidates every `months` query for this household (list + each period) — not just the
 * household doc — since an open month's buckets/analysis are computed server-side from the live
 * `household.buckets`, not from a copy. Without this, the checklist/overview would keep showing
 * stale bucket bars until some unrelated refetch happened to occur (P1-09 acceptance criterion:
 * "adding 'Giving 10' ... re-renders overview").
 */
export function usePutBuckets(householdId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    meta: { successMessage: 'Budget settings saved' },
    mutationFn: (input: PutBucketsInput) =>
      api.put<BucketDef[]>(`/households/${householdId}/buckets`, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['households', householdId] });
      void queryClient.invalidateQueries({ queryKey: ['h', householdId, 'months'] });
    },
  });
}

export interface PersonInput {
  name: string;
  role?: 'adult' | 'child';
  colorIndex?: number;
}

export function useAddPerson(householdId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    meta: { successMessage: 'Family member recorded' },
    mutationFn: (input: PersonInput) =>
      api.post<Person>(`/households/${householdId}/people`, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['households', householdId] });
    },
  });
}

export function usePatchPerson(householdId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    meta: { successMessage: 'Family member updated' },
    mutationFn: ({ id, ...input }: Partial<PersonInput> & { id: string }) =>
      api.patch<Person>(`/households/${householdId}/people/${id}`, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['households', householdId] });
    },
  });
}

export function useRemovePerson(householdId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    meta: { successMessage: 'Family member removed' },
    mutationFn: (id: string) => api.delete<void>(`/households/${householdId}/people/${id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['households', householdId] });
    },
  });
}

export interface CreateInviteInput {
  personId?: string;
  email: string;
  role?: 'owner' | 'member';
}

export interface Invite {
  id: string;
  email: string;
  role: string;
  status: string;
  expiresAt: string;
  createdAt: string;
}

export function useCreateInvite(householdId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    meta: { successMessage: 'Invitation created' },
    mutationFn: (input: CreateInviteInput) =>
      api.post<Invite>(`/households/${householdId}/invites`, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['h', householdId, 'invites'] });
    },
  });
}

export function useInvites(householdId: string, enabled = true) {
  return useQuery({
    queryKey: ['h', householdId, 'invites'] as const,
    queryFn: () => api.get<Invite[]>(`/households/${householdId}/invites`),
    enabled: !!householdId && enabled,
  });
}

export interface HouseholdMember {
  userId: string;
  name: string;
  email: string;
  avatarUrl?: string;
  role: 'owner' | 'member';
}
export function useHouseholdMembers(hid: string, enabled: boolean) {
  return useQuery({
    queryKey: ['household-members', hid],
    queryFn: () => api.get<HouseholdMember[]>(`/households/${hid}/members`),
    enabled: !!hid && enabled,
  });
}
export function useRemoveHouseholdMember(hid: string) {
  const client = useQueryClient();
  return useMutation({
    meta: { successMessage: 'Household member removed' },
    mutationFn: (userId: string) => api.delete<void>(`/households/${hid}/members/${userId}`),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['household-members', hid] });
      void client.invalidateQueries({ queryKey: ['households', hid] });
      void client.invalidateQueries({ queryKey: ['household-mailboxes', hid] });
    },
  });
}
export function useRevokeInvite(hid: string) {
  const client = useQueryClient();
  return useMutation({
    meta: { successMessage: 'Invitation revoked' },
    mutationFn: (id: string) => api.delete<void>(`/households/${hid}/invites/${id}`),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['h', hid, 'invites'] });
    },
  });
}

export function useUpdatePersonEmail(hid: string) {
  const client = useQueryClient();
  return useMutation({
    meta: { successMessage: 'Member email updated' },
    mutationFn: ({ id, email }: { id: string; email: string }) =>
      api.patch<void>(`/households/${hid}/people/${id}/email`, { email }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['households', hid] });
      void client.invalidateQueries({ queryKey: ['household-members', hid] });
    },
  });
}

/** Link an existing signed-in member to the family list without changing their access. */
export function useAddMemberToFamily(householdId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    meta: { successMessage: 'Family member added' },
    mutationFn: (userId: string) =>
      api.post<void>(`/households/${householdId}/members/${userId}/family-profile`),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['households', householdId] });
    },
  });
}
